import { type ArchivedFilter, PROJECT_COLORS } from '@taim/shared';
import { Check, EllipsisVertical, Search } from 'lucide-react';
import { type ComponentProps, type FormEvent, type ReactNode, useEffect, useId, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

/** A readable message for a failed request. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiRequestError) {
    // The API names the entity for catalog conflicts; keep a readable fallback for the generic one.
    if (e.code === 'conflict' && e.message === 'This already exists') {
      return 'A record with this name already exists';
    }
    return e.message;
  }
  return e instanceof Error ? e.message : 'Something went wrong';
}

export function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="text-destructive text-sm">
      {errorMessage(error)}
    </p>
  );
}

/** Native select styled like the other inputs: dependable with keyboards, screen readers and tests. */
export function NativeSelect({ className, ...props }: ComponentProps<'select'>) {
  return (
    <select
      className={cn(
        'h-9 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

/** Clockify's "Active / Archived / All" list filter. */
export function ArchivedSelect({
  value,
  onChange,
}: {
  value: ArchivedFilter;
  onChange: (v: ArchivedFilter) => void;
}) {
  return (
    <NativeSelect
      aria-label="Show"
      value={value}
      onChange={(e) => onChange(e.target.value as ArchivedFilter)}
    >
      <option value="false">Active</option>
      <option value="true">Archived</option>
      <option value="all">All</option>
    </NativeSelect>
  );
}

export function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 size-4 text-muted-foreground" />
      <Input
        type="search"
        placeholder="Search by name"
        className="w-56 bg-background pl-8"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export type RowAction = { label: string; onSelect: () => void; destructive?: boolean };

/** The ⋮ menu at the end of a table row. */
export function RowMenu({ name, actions }: { name: string; actions: RowAction[] }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${name}`}>
          <EllipsisVertical className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((a) => (
          <DropdownMenuItem
            key={a.label}
            onSelect={a.onSelect}
            className={cn(a.destructive && 'text-destructive focus:text-destructive')}
          >
            {a.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Standard Edit / Archive / Restore / Delete actions (Delete only once archived, like Clockify). */
export function archiveActions(
  archived: boolean,
  handlers: { onEdit: () => void; onArchive: (archived: boolean) => void; onDelete: () => void },
): RowAction[] {
  return [
    { label: 'Edit', onSelect: handlers.onEdit },
    archived
      ? { label: 'Restore', onSelect: () => handlers.onArchive(false) }
      : { label: 'Archive', onSelect: () => handlers.onArchive(true) },
    ...(archived ? [{ label: 'Delete', onSelect: handlers.onDelete, destructive: true }] : []),
  ];
}

export function ConfirmDelete({
  target,
  what,
  onCancel,
  onConfirm,
}: {
  /** Name of the item to delete; the dialog is open while set. */
  target: string | null;
  what: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={target !== null} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {what}</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete {target}? This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={onConfirm}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export type NameDialogValue = { name: string; note?: string | null };

/** Edit dialog for things that only have a name (and optionally a note): clients, tags, tasks. */
export function NameDialog({
  title,
  initial,
  withNote = false,
  onClose,
  onSave,
}: {
  title: string;
  /** The dialog is open while set. */
  initial: NameDialogValue | null;
  withNote?: boolean;
  onClose: () => void;
  onSave: (v: NameDialogValue) => Promise<unknown>;
}) {
  const nameId = useId();
  const noteId = useId();
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(initial?.name ?? '');
    setNote(initial?.note ?? '');
    setError(null);
  }, [initial]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(withNote ? { name, note: note.trim() || null } : { name });
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={initial !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor={nameId}>Name</Label>
            <Input id={nameId} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          {withNote && (
            <div className="grid gap-2">
              <Label htmlFor={noteId}>Note</Label>
              <Textarea id={noteId} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          )}
          <FormError error={error} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              SAVE
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ColorDot({ color, className }: { color: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block size-2.5 shrink-0 rounded-full', className)}
      style={{ backgroundColor: color }}
    />
  );
}

/** Clockify's project colour swatches. */
export function ColorPicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (c: string) => void;
  disabled?: boolean;
}) {
  const group = useId();
  return (
    <fieldset className="flex flex-wrap gap-1.5" disabled={disabled}>
      <legend className="sr-only">Colour</legend>
      {PROJECT_COLORS.map((c) => {
        const selected = c.toLowerCase() === value.toLowerCase();
        return (
          <span
            key={c}
            className="relative flex size-6 items-center justify-center rounded-full has-[:disabled]:opacity-50 has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50"
            style={{ backgroundColor: c }}
          >
            <input
              type="radio"
              name={group}
              value={c}
              checked={selected}
              onChange={() => onChange(c)}
              aria-label={`Colour ${c}`}
              className="absolute inset-0 cursor-pointer appearance-none rounded-full outline-none disabled:cursor-default"
            />
            {selected && <Check className="pointer-events-none size-3.5 text-white" />}
          </span>
        );
      })}
    </fieldset>
  );
}

/** White card that wraps list tables, as on Clockify's list pages. */
export function ListCard({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="border-b bg-muted/60 px-4 py-2 text-muted-foreground text-sm">{label}</div>
      {children}
    </div>
  );
}
